import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, catchError, of, takeUntil, tap } from 'rxjs';
import { DateRangePresetType, ProcessingState } from '@services/core-services/models';
import { ErrorHandlerService } from '@services/core-services/error-handler.service';
import {
  CustomerAnalyticsService,
  CustomerStats,
  TopCustomerStats,
} from '@services/analytics-services/customers';
import { SubsequentPurchasesStats } from '@services/analytics-services/customers/subsequest-purchases-stats'; // Path might need adjustment if file moved/renamed
import { AggregatedStatDataService } from '@services/analytics-services/core/analytics-aggregated-stat.service';
import { DynamicQueryService } from '@services/analytics-services/core/dynamic-query.service';
import { QueryType, ChartType } from '@services/analytics-services/core/models/matrix-stats';
import { DashboardStore } from '../dashboard/dashboard.store';
import { AnalyticsInfo, AnalyticsRangeContext } from '@services/analytics-services/core'; // Import AnalyticsInfo and AnalyticsRangeContext

@Injectable()
export class DashboardCustomersStore implements OnDestroy {
  private customerAnalytics = new BehaviorSubject<AnalyticsInfo<CustomerStats> | undefined>(
    undefined
  );
  private topCustomer = new BehaviorSubject<TopCustomerStats[] | undefined>(undefined);
  private subsequentPurchases = new BehaviorSubject<SubsequentPurchasesStats[] | undefined>(
    undefined
  );

  private customerAnalyticsLoader = new BehaviorSubject<ProcessingState>(ProcessingState.Loading);
  private topCustomerLoader = new BehaviorSubject<ProcessingState>(ProcessingState.Loading);
  private subsequentPurchasesLoader = new BehaviorSubject<ProcessingState>(ProcessingState.Loading);

  private customerAnalyticsErrorMessage = new BehaviorSubject<string>('');
  private topCustomerErrorMessage = new BehaviorSubject<string>('');
  private subsequentPurchasesErrorMessage = new BehaviorSubject<string>('');

  // Subject for unsubscribing
  private destroy$ = new Subject<void>();

  constructor(
    private readonly dashboardStore: DashboardStore,
    private readonly aggregatedStatDataService: AggregatedStatDataService,
    private readonly dynamicQueryService: DynamicQueryService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly customerAnalyticsService: CustomerAnalyticsService
  ) {
    this.dashboardStore
      .getRangeContext()
      .pipe(
        tap((context: AnalyticsRangeContext) => {
          this.customerAnalyticsErrorMessage.next('');
          this.topCustomerErrorMessage.next('');
          this.subsequentPurchasesErrorMessage.next('');
          this.loadCustomerAnalytics(context).subscribe();
          this.loadTopCustomer(context).subscribe();
          this.loadSubsequentPurchases(context).subscribe();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getCustomerAnalytics(): Observable<AnalyticsInfo<CustomerStats> | undefined> {
    return this.customerAnalytics.asObservable();
  }

  getCustomerAnalyticsLoader(): Observable<ProcessingState> {
    return this.customerAnalyticsLoader.asObservable();
  }

  getCustomerAnalyticsErrorMessage(): Observable<string> {
    return this.customerAnalyticsErrorMessage.asObservable();
  }

  getTopCustomers(): Observable<TopCustomerStats[] | undefined> {
    return this.topCustomer.asObservable();
  }

  getSubsequentPurchases(): Observable<SubsequentPurchasesStats[] | undefined> {
    return this.subsequentPurchases.asObservable();
  }

  getTopCustomerLoader(): Observable<ProcessingState> {
    return this.topCustomerLoader.asObservable();
  }

  getsubsequentPurchasesLoader(): Observable<ProcessingState> {
    return this.subsequentPurchasesLoader.asObservable();
  }

  getTopCustomerErrorMessage(): Observable<string> {
    return this.topCustomerErrorMessage.asObservable();
  }

  getSubsequentPurchasesErrorMessage(): Observable<string> {
    return this.subsequentPurchasesErrorMessage.asObservable();
  }

  loadCustomerAnalytics(
    context: AnalyticsRangeContext
  ): Observable<AnalyticsInfo<CustomerStats> | undefined> {
    this.customerAnalyticsLoader.next(ProcessingState.Loading);
    const { range, compare, interval, preset } = context;

    return this.customerAnalyticsService
      .getAnalytics({
        preset: preset as DateRangePresetType,
        range,
        compare,
        interval,
      })
      .pipe(
        tap(analytics => {
          this.customerAnalyticsLoader.next(ProcessingState.Success);
          this.customerAnalytics.next(analytics);
        }),
        catchError(error => {
          this.customerAnalyticsLoader.next(ProcessingState.Error);
          this.handleError(
            'Error loading customer analytics',
            error,
            this.customerAnalyticsErrorMessage
          );
          this.customerAnalytics.next(undefined);
          return of(undefined);
        })
      );
  }

  loadTopCustomer(context: AnalyticsRangeContext): Observable<TopCustomerStats[]> {
    this.topCustomerLoader.next(ProcessingState.Loading);
    const { range, interval, preset } = context;

    return this.aggregatedStatDataService
      .getAggregatedStatdata({
        preset: preset as DateRangePresetType,
        date_from: range.start,
        date_to: range.end,
        interval,
        query_type: QueryType.STATS,
        chart_type: ChartType.TOP_CUSTOMERS,
        timezone: this.dynamicQueryService.getUserTimezone(),
      })
      .pipe(
        tap((customers: TopCustomerStats[]) => {
          this.topCustomerLoader.next(ProcessingState.Success);
          this.topCustomer.next(customers);
        }),
        catchError(error => {
          this.topCustomerLoader.next(ProcessingState.Error);
          this.handleError('Error loading top customers', error, this.topCustomerErrorMessage);
          this.topCustomer.next(undefined);
          return of([] as TopCustomerStats[]);
        })
      );
  }

  loadSubsequentPurchases(context: AnalyticsRangeContext): Observable<SubsequentPurchasesStats[]> {
    this.subsequentPurchasesLoader.next(ProcessingState.Loading);
    const { range, interval, preset } = context;

    return this.aggregatedStatDataService
      .getAggregatedStatdata({
        preset: preset as DateRangePresetType,
        date_from: range.start,
        date_to: range.end,
        interval,
        query_type: QueryType.STATS,
        chart_type: ChartType.AVERAGE_TIME_BETWEEN_SUBSEQUENT_PURCHASES,
        timezone: this.dynamicQueryService.getUserTimezone(),
      })
      .pipe(
        tap((purchases: SubsequentPurchasesStats[]) => {
          this.subsequentPurchasesLoader.next(ProcessingState.Success);
          this.subsequentPurchases.next(purchases);
        }),
        catchError(error => {
          this.subsequentPurchasesLoader.next(ProcessingState.Error);
          this.handleError(
            'Error loading subsequent purchases',
            error,
            this.subsequentPurchasesErrorMessage
          );
          this.subsequentPurchases.next(undefined);
          return of([] as SubsequentPurchasesStats[]);
        })
      );
  }

  // --- Error Handling ---
  private handleError(message: string, error: any, errorSubject: BehaviorSubject<string>): void {
    const errorMessage = this.errorHandlerService.handleServerError(error);
    errorSubject.next(typeof errorMessage === 'string' ? errorMessage : message);
    console.error(message, error);
  }
}
